import { useCallback } from 'react';
import { toast } from 'sonner';
import { builtEnvironment } from '@shared/builtEnvironment';
import { isRunningLocal } from '@shared/isRunningEnvironment';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';

interface UseIntakeFormActionsResult {
  navigateToUpdateIntakeForm: (clientId: string) => Promise<void>;
}

export function useIntakeFormActions(): UseIntakeFormActionsResult {
  const { getClerkToken } = useClerkClientAuth();
  const isEdjPilot = useIsEdjPilotAdvisor();

  const navigateToUpdateIntakeForm = useCallback(
    async (clientId: string) => {
      if (isEdjPilot) {
        return;
      }
      try {
        const token = await getClerkToken();
        if (!token) {
          toast.error('Authentication required', { description: 'Please sign in again.' });
          return;
        }
        const apiBase = `/${isRunningLocal() ? 'dev' : builtEnvironment}/api`;

        await fetch(`${apiBase}/reopen-form/${clientId}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const response = await fetch(`${apiBase}/client/${clientId}/form-reference`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage =
            errorData?.errorMessage ?? 'Failed to load intake form. Please try again.';
          toast.error('Failed to load intake form', {
            description: errorMessage,
          });
          return;
        }

        const { formReferenceId } = await response.json();
        const baseUrl = window.location.origin;
        const formUrl = `${baseUrl}/onboarding-form/${formReferenceId}`;
        window.open(formUrl, '_blank', 'noopener,noreferrer');
      } catch (err) {
        const errorMessage =
          'An error occurred while loading the intake form. Please try again.';
        toast.error('Failed to load intake form', {
          description: errorMessage,
        });
      }
    },
    [getClerkToken, isEdjPilot],
  );

  return {
    navigateToUpdateIntakeForm,
  };
}
